import React, { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Plus, Trash2, Copy, ArrowUp, ArrowDown, Layers as LayersIcon, History, Merge } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BLENDS, blendLabel } from "./tools";

function Thumb({ canvas, v }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c || !canvas) return;
    const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height);
    const s = Math.min(c.width / canvas.width, c.height / canvas.height);
    ctx.drawImage(canvas, (c.width - canvas.width * s) / 2, (c.height - canvas.height * s) / 2, canvas.width * s, canvas.height * s);
  }, [canvas, v]);
  return <canvas ref={ref} width={44} height={32} className="checker rounded-sm border border-black" />;
}

export const Panel = ({ title, icon: I, children, right }) => (
  <div className="border-b border-black">
    <div className="h-8 flex items-center gap-2 px-3 bg-[#2b2d31] text-[11px] font-medium text-zinc-300 border-b border-black">{I && <I className="w-3.5 h-3.5 text-zinc-400" />}{title}<div className="ml-auto flex gap-1">{right}</div></div>
    {children}
  </div>
);

const SW = ["#000000", "#ffffff", "#fbbf24", "#f59e0b", "#ef4444", "#f472b6", "#a78bfa", "#6366f1", "#38bdf8", "#22d3ee", "#34d399", "#84cc16", "#78350f", "#57534e", "#9ca3af", "#1f2937", "#fde68a", "#fecaca", "#bfdbfe", "#bbf7d0"];

export function ColorPanel({ fg, bg, setFg, setBg, recent }) {
  return (
    <Panel title="Color">
      <div className="p-3 flex gap-3">
        <div className="relative w-16 h-16 shrink-0">
          <label className="absolute left-0 top-0 w-10 h-10 rounded border-2 border-white/80 shadow-lg cursor-pointer z-10" style={{ background: fg }} title="Foreground" data-testid="fg-swatch"><input type="color" value={fg} onChange={(e) => setFg(e.target.value)} className="opacity-0 w-0 h-0" /></label>
          <label className="absolute right-0 bottom-0 w-10 h-10 rounded border-2 border-zinc-500 cursor-pointer" style={{ background: bg }} title="Background"><input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="opacity-0 w-0 h-0" /></label>
        </div>
        <div className="flex-1 space-y-2">
          <input value={fg} onChange={(e) => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && setFg(e.target.value)} className="w-full h-7 bg-black/40 rounded px-2 text-xs font-mono outline-none focus:ring-1 ring-sky-500" data-testid="fg-hex" />
          <div className="flex gap-1 flex-wrap">{recent.map((c, i) => <button key={c + i} onClick={() => setFg(c)} className="w-4 h-4 rounded-sm border border-black" style={{ background: c }} />)}</div>
        </div>
      </div>
      <div className="px-3 pb-3 grid grid-cols-10 gap-1">{SW.map((c) => <button key={c} onClick={() => setFg(c)} onContextMenu={(e) => { e.preventDefault(); setBg(c); }} className="aspect-square rounded-sm border border-black hover:scale-110 transition-transform" style={{ background: c }} title={`${c} (right-click = background)`} />)}</div>
    </Panel>
  );
}

export function LayersPanel({ layers, active, setActive, v, act }) {
  const cur = layers.find((l) => l.id === active);
  const [rename, setRename] = useState(null);
  const ib = "w-6 h-6 rounded flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10";
  return (
    <Panel title="Layers" icon={LayersIcon}>
      {cur && (
        <div className="p-2 space-y-2 border-b border-black">
          <div className="flex items-center gap-2">
            <Select value={cur.blend} onValueChange={(b) => act.setProp("blend", b)}>
              <SelectTrigger className="h-7 text-[11px] bg-black/40 border-black flex-1" data-testid="blend-select"><SelectValue /></SelectTrigger>
              <SelectContent>{BLENDS.map((b) => <SelectItem key={b} value={b} className="text-xs">{blendLabel(b)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-zinc-400">Opacity<Slider className="flex-1" min={0} max={1} step={0.01} value={[cur.opacity]} onValueChange={([o]) => act.setProp("opacity", o, true)} onValueCommit={() => act.commit("Layer Opacity")} /><span className="w-8 text-right font-mono">{Math.round(cur.opacity * 100)}%</span></div>
        </div>
      )}
      <div className="max-h-[260px] overflow-auto thin-scroll" data-testid="layers-list">
        {[...layers].reverse().map((l) => (
          <div key={l.id} onClick={() => setActive(l.id)} data-testid={`layer-${l.name}`} className={`flex items-center gap-2 px-2 py-1.5 border-b border-black/60 cursor-pointer ${l.id === active ? "bg-sky-600/30" : "hover:bg-white/5"}`}>
            <button onClick={(e) => { e.stopPropagation(); act.toggleVis(l.id); }} className="text-zinc-400 hover:text-white">{l.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 opacity-50" />}</button>
            <Thumb canvas={l.canvas} v={v} />
            {rename === l.id ? <input autoFocus defaultValue={l.name} onBlur={(e) => { act.rename(l.id, e.target.value); setRename(null); }} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} className="flex-1 bg-black/40 text-xs px-1 rounded outline-none" />
              : <span onDoubleClick={() => setRename(l.id)} className="flex-1 text-xs truncate">{l.name}</span>}
            {l.blend !== "source-over" && <span className="text-[9px] text-zinc-500">{blendLabel(l.blend)}</span>}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-end gap-1 p-1.5 bg-[#26282c]">
        <button className={ib} title="Move up" onClick={() => act.move(1)}><ArrowUp className="w-3.5 h-3.5" /></button>
        <button className={ib} title="Move down" onClick={() => act.move(-1)}><ArrowDown className="w-3.5 h-3.5" /></button>
        <button className={ib} title="Merge down" onClick={act.mergeDown}><Merge className="w-3.5 h-3.5" /></button>
        <button className={ib} title="Duplicate" onClick={act.duplicate} data-testid="dup-layer-btn"><Copy className="w-3.5 h-3.5" /></button>
        <button className={ib} title="New layer" onClick={() => act.add()} data-testid="new-layer-btn"><Plus className="w-3.5 h-3.5" /></button>
        <button className={ib} title="Delete layer" onClick={act.remove} data-testid="delete-layer-btn"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </Panel>
  );
}

export function HistoryPanel({ history, idx, jump }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [history.length]);
  return (
    <Panel title="History" icon={History}>
      <div ref={ref} className="max-h-[150px] overflow-auto thin-scroll" data-testid="history-list">
        {history.map((h, i) => <button key={i} onClick={() => jump(i)} className={`w-full text-left px-3 py-1 text-xs border-b border-black/40 ${i === idx ? "bg-sky-600/30 text-white" : i > idx ? "text-zinc-600" : "text-zinc-300 hover:bg-white/5"}`}>{h.label}</button>)}
      </div>
    </Panel>
  );
}

export function AdjustDialog({ open, onClose, onPreview, onApply }) {
  const [v, setV] = useState({ brightness: 100, contrast: 100, saturate: 100, hue: 0, blur: 0, sepia: 0 });
  useEffect(() => { if (open) setV({ brightness: 100, contrast: 100, saturate: 100, hue: 0, blur: 0, sepia: 0 }); }, [open]);
  const f = `brightness(${v.brightness}%) contrast(${v.contrast}%) saturate(${v.saturate}%) hue-rotate(${v.hue}deg) blur(${v.blur}px) sepia(${v.sepia}%)`;
  useEffect(() => { if (open) onPreview(f); }, [f, open]); // eslint-disable-line
  const rows = [["brightness", "Brightness", 0, 200], ["contrast", "Contrast", 0, 200], ["saturate", "Saturation", 0, 300], ["hue", "Hue", -180, 180], ["blur", "Gaussian Blur", 0, 30], ["sepia", "Warm Tone", 0, 100]];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onPreview(null); onClose(); } }}>
      <DialogContent className="max-w-md bg-[#232428] border-black">
        <DialogHeader><DialogTitle className="text-base">Adjustments</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {rows.map(([k, l, a, b]) => (
            <div key={k}><div className="flex justify-between text-xs text-zinc-400 mb-2"><span>{l}</span><span className="font-mono text-zinc-200">{v[k]}</span></div>
              <Slider min={a} max={b} step={1} value={[v[k]]} onValueChange={([x]) => setV((s) => ({ ...s, [k]: x }))} data-testid={`adjust-${k}`} /></div>
          ))}
        </div>
        <DialogFooter>
          <button onClick={() => { onPreview(null); onClose(); }} className="h-9 px-4 rounded border border-white/10 text-sm hover:bg-white/5">Cancel</button>
          <button onClick={() => { onApply(f); onClose(); }} className="h-9 px-4 rounded bg-sky-500 text-white text-sm hover:bg-sky-400" data-testid="adjust-apply">Apply</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const PRESETS = [
  { n: "Instagram Post", w: 1080, h: 1080 }, { n: "Instagram Story", w: 1080, h: 1920 }, { n: "YouTube Thumbnail", w: 1280, h: 720 },
  { n: "Gig Flyer", w: 1200, h: 1600 }, { n: "Album Cover", w: 1400, h: 1400 }, { n: "Web Banner", w: 1600, h: 600 },
];

export function NewDocDialog({ open, onClose, onCreate }) {
  const [p, setP] = useState(0); const [bg, setBg] = useState("white");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#232428] border-black">
        <DialogHeader><DialogTitle className="text-base">New Document</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2">
          {PRESETS.map((x, i) => (
            <button key={x.n} onClick={() => setP(i)} data-testid={`preset-${i}`} className={`p-3 rounded border text-left ${p === i ? "border-sky-500 bg-sky-500/10" : "border-white/10 hover:bg-white/5"}`}>
              <div className="h-10 flex items-center justify-center mb-2"><div className="border border-zinc-400" style={{ width: (x.w / Math.max(x.w, x.h)) * 36, height: (x.h / Math.max(x.w, x.h)) * 36 }} /></div>
              <div className="text-xs">{x.n}</div><div className="text-[10px] text-zinc-500 font-mono">{x.w}×{x.h}</div>
            </button>
          ))}
        </div>
        <div className="flex gap-2 text-xs">{["white", "black", "transparent"].map((b) => <button key={b} onClick={() => setBg(b)} className={`px-3 h-8 rounded border capitalize ${bg === b ? "border-sky-500 bg-sky-500/10" : "border-white/10"}`}>{b}</button>)}</div>
        <DialogFooter><button onClick={() => { onCreate(PRESETS[p], bg); onClose(); }} className="h-9 px-4 rounded bg-sky-500 text-white text-sm hover:bg-sky-400" data-testid="create-doc-btn">Create</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
